import PropTypes from 'prop-types'
import { useState, useEffect, useRef, useMemo } from 'react'
import Spinner from 'react-bootstrap/Spinner'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { ID_PERFIL_ADMIN_EBP } from '../../constants/perfiles.js'
import { aplicarFiltrosGenerico } from '../../helpers/filtros/aplicarFiltrosGenerico'
import { MAPA_PEDIDOS } from '../../helpers/filtros/mapaPedidos.js'
import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import { useAuth } from '../../hooks/useAuth'
import { usePaginacion } from '../hooks/usePaginacion'
import ComponenteError from '../publicaciones/ComponenteError'
import { FiltrosContainer } from '../reutilizables/filtros/FiltrosContainer.jsx'
import { Paginacion } from '../reutilizables/paginacion/Paginacion'
import { Pedido } from '../reutilizables/pedidos/Pedido'
import * as pedidosService from '../servicios/pedidosService'
import { buscarMedioPago } from '../servicios/publicacionesService'
import * as ventasService from '../servicios/ventasService'
import BordePrincipal from '../ui/BordePrincipal'
import styles from './Pedidos.module.css'

const PAGINA_ITEMS = 15

const Pedidos = ({ esVentas }) => {
  const topRef = useRef(null)
  const { loginDataContext: { idUsuario: usuario, empid, idPerfil } = {}, esPerfilAdmin } =
    useAuth()
  const esAdminEBP = useLocation().pathname === '/PedidosEBM' && idPerfil === ID_PERFIL_ADMIN_EBP
  const [pedidos, setPedidos] = useState([])

  const [textoBusqueda, setTextoBusqueda] = useState('')
  const [mediosPago, setMediosPago] = useState([])
  const [cargando, setCargando] = useState(true)
  const { filtrosLocales } = useSelector((state) => state.filters)

  const pedidosFiltrados = useMemo(
    () =>
      aplicarFiltrosGenerico(
        pedidos,
        {
          ...filtrosLocales,
          textoBusqueda,
          esVentas,
          idUsuario: usuario,
          esAdmin: esPerfilAdmin
        },
        MAPA_PEDIDOS
      ),
    [pedidos, filtrosLocales, textoBusqueda, esVentas, usuario, esPerfilAdmin]
  )
  const sinResultados = pedidosFiltrados.length === 0

  const {
    itemsActual: pedidosPaginados,
    paginaActual,
    setPaginaActual,
    itemsPorPagina,
    setItemsPorPagina,
    totalPaginas
  } = usePaginacion(pedidosFiltrados, PAGINA_ITEMS)

  useEffect(() => {
    setPaginaActual(1)
  }, [pedidosFiltrados.length])

  const fetchData = async (search) => {
    const { data } = esPerfilAdmin
      ? await pedidosService.buscarPorEmpresa(empid, search)
      : await pedidosService.buscarPorUsuario(usuario, search)
    const dataProcesada = data.map((pedido) => ({
      ...pedido,
      pedidoItems: pedido.pedidoItems.map((item) => ({
        ...item,
        medioPago: obtenerMedioPago(item.idMedioPago),
        vendedorRazonSocial: item.razonSocial,
        compradorRazonSocial: item.compradorRazonSocial,
        empidComprador: item.compradorEmpId
      }))
    }))
    setPedidos(dataProcesada)
  }

  const cargarMediosPago = async () => {
    const { data } = await buscarMedioPago()
    setMediosPago(data || [])
  }

  const obtenerMedioPago = (idMedioPago) => {
    if (!mediosPago || mediosPago.length === 0) {
      return ''
    }

    const medioPago = mediosPago.find((mp) => mp.idMedioPago === idMedioPago)
    return medioPago?.nombre || ''
  }

  const fetchPedidos = async (search) => {
    const { data } = esPerfilAdmin
      ? await ventasService.buscarPorEmpresa(empid, search)
      : await ventasService.buscarPorUsuario(usuario, search)
    const pedidosProcesados = data
      .map((pedido) => ({
        ...pedido,
        pedidoItems: Array.isArray(pedido.ventaItems)
          ? pedido.ventaItems.map((element) => ({
              ...element,
              alicuota: element.alicuota,
              cantidad: element.cantidad,
              estados: pedido.estados,
              idPedido: pedido.idPedido,
              compradorNombre: `${element.nombre} ${element.apellido}`,
              razonSocial: element.razonSocial ?? pedido.razonSocial,
              vendedorNombre: element.vendedorNombre,
              empidVendedor: element.empidVendedor,
              idVendedor: pedido.idVendedor,
              empidComprador: element.empID,
              compradorRazonSocial: element.razonSocial,
              vendedorEMail: element.email,
              vendedorTelefono: element.telefono,
              vendedorCalle: element.calle,
              vendedorCalleAltura: element.calleAltura,
              vendedorPiso: element.piso,
              vendedorCP: element.cp,
              descripcionEstado: element.estadoDescripcion,
              entregaLocalidad: element.localidad,
              entregaCalle: element.calle,
              entregaCalleAltura: element.calleAltura,
              entregaProvincia: element.provincia,
              medioPago: obtenerMedioPago(element.idMedioPago),
              precioEnvio: element.precioEnvio ?? pedido.precioEnvio ?? 0,
              envioTipo: element.envioTipo ?? pedido.envioTipo ?? '',
              idMedioEnvio: element.idMedioEnvio ?? pedido.idMedioEnvio ?? null
            }))
          : []
      }))
      .sort(
        (a, b) =>
          new Date(b.ventaItems?.[0]?.fechaPedido || 0) -
          new Date(a.ventaItems?.[0]?.fechaPedido || 0)
      )
    setPedidos(pedidosProcesados)
  }

  const fetchTodosPedidos = async (search) => {
    const { data } = await pedidosService.BuscarTodoPedidos(search)
    const dataProcesada = data.map((pedido) => ({
      ...pedido,
      pedidoItems: pedido.pedidoItems.map((item) => ({
        ...item,
        medioPago: obtenerMedioPago(item.idMedioPago),
        vendedorRazonSocial: item.razonSocial,
        compradorRazonSocial: item.compradorRazonSocial,
        empidComprador: item.compradorEmpId
      }))
    }))
    setPedidos(dataProcesada)
  }

  const handleBuscar = async () => {
    try {
      if (esAdminEBP && !esVentas) {
        await fetchTodosPedidos('')
      } else {
        if (esVentas) {
          await fetchPedidos('')
        } else {
          await fetchData('')
        }
      }
      setPaginaActual(1)
    } catch (error) {
      const mensaje = 'Error al buscar pedidos.'
      toastErrorLog(error, mensaje, 'pedidos')
    }
    setCargando(false)
  }

  useEffect(() => {
    if (mediosPago.length !== 0) {
      setPedidos([])
      setCargando(true)
      setTextoBusqueda('')
      if (esPerfilAdmin && !empid) {
        setCargando(false)
        return
      }
      handleBuscar()
    }
  }, [mediosPago, esVentas, empid, esPerfilAdmin])

  useEffect(() => {
    cargarMediosPago()
  }, [])

  const contextoFiltros = useMemo(() => ({ esVentas, pedidos }), [esVentas, pedidos])

  return (
    <div>
      <BordePrincipal
        titulo={esAdminEBP ? 'Todas las Compras' : esVentas ? 'Mis Ventas' : 'Mis Compras'}
      >
        <div className={styles.layout} ref={topRef}>
          <FiltrosContainer
            arrayAFiltrar={pedidos}
            mapaFiltros={MAPA_PEDIDOS}
            contextoFiltros={contextoFiltros}
            filtrosVisibles={[
              (esPerfilAdmin || esVentas) && 'comprador',
              (esPerfilAdmin || !esVentas) && 'vendedor',
              'idPedidoItemEstado',
              'idPagoEstado',
              'precio',
              'fechaDesde',
              'fechaHasta'
            ]}
            buscador={{
              onChange: () => {},
              placeholder: MAPA_PEDIDOS.textoBusqueda.placeholder,
              valor: textoBusqueda,
              setBusqueda: setTextoBusqueda
            }}
            esVentas={esVentas}
            esAdministrador={esAdminEBP}
          />
          <div className="col">
            {cargando ? (
              <div className="d-flex justify-content-center p-4">
                <Spinner animation="border" role="status" aria-label="Cargando" />
              </div>
            ) : !sinResultados ? (
              pedidosPaginados.map((pedido) => (
                <Pedido
                  key={pedido.idPedido}
                  pedido={pedido}
                  esVentas={esVentas}
                  mediosPago={mediosPago}
                  esAdministrador={esAdminEBP}
                />
              ))
            ) : !textoBusqueda ? (
              esVentas ? (
                <div className="row">
                  <ComponenteError tipo="I" idAviso={22} />
                </div>
              ) : (
                <div className="row">
                  <ComponenteError tipo="I" idAviso={10} />
                </div>
              )
            ) : (
              <div className="row">
                <ComponenteError tipo="I" idAviso={24} />
              </div>
            )}
          </div>
        </div>
      </BordePrincipal>
      {totalPaginas > 1 && (
        <Paginacion
          totalPaginas={totalPaginas}
          paginaActual={paginaActual}
          onCambioPagina={(num) => {
            setPaginaActual(num)
            topRef.current?.scrollIntoView({ behavior: 'smooth' })
          }}
          itemsPorPagina={itemsPorPagina}
          onCambioItemsPorPagina={(num) => {
            setItemsPorPagina(num)
            setPaginaActual(1)
          }}
        />
      )}
    </div>
  )
}

export default Pedidos

Pedidos.propTypes = {
  esVentas: PropTypes.bool.isRequired,
  esAdministrador: PropTypes.bool.isRequired
}
